import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Megaphone, Trash2, Clock, AlertTriangle, Info, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITA = [
  { value: 'normale', label: 'Normale', icon: Info, color: 'bg-sky-100 text-sky-700' },
  { value: 'importante', label: 'Importante', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700' },
  { value: 'urgente', label: 'Urgente', icon: AlertCircle, color: 'bg-red-100 text-red-700' },
];

const Avvisi = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ titolo: '', messaggio: '', priorita: 'normale' });
  const { hasRole } = useAuth();

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli avvisi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.titolo || !form.messaggio) return;
    try {
      await axios.post(`${API_URL}/api/announcements`, form);
      toast.success('Avviso inviato a tutti');
      setShowModal(false);
      setForm({ titolo: '', messaggio: '', priorita: 'normale' });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Errore nella creazione dell\'avviso');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo avviso?')) return;
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`);
      toast.success('Avviso eliminato');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/announcements/${id}/read`);
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, letto: true } : a));
    } catch (error) {
      console.error(error);
    }
  };

  const getPriorita = (val) => PRIORITA.find(p => p.value === val) || PRIORITA[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="avvisi-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Avvisi</h1>
          <p className="text-slate-500 mt-1">Comunicazioni e aggiornamenti</p>
        </div>
        {hasRole(['super_admin', 'admin']) && (
          <Button onClick={() => setShowModal(true)} data-testid="create-avviso-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Avviso
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />)}
        </div>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun avviso pubblicato</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => {
            const prio = getPriorita(a.priorita);
            const Icon = prio.icon;
            return (
              <Card
                key={a.id}
                className={`transition-all ${!a.letto ? 'ring-2 ring-sky-200 bg-sky-50/30' : ''}`}
                data-testid={`avviso-${a.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`mt-0.5 p-2 rounded-lg ${prio.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1" onClick={() => !a.letto && handleMarkRead(a.id)} style={{cursor: !a.letto ? 'pointer' : 'default'}}>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{a.titolo}</h3>
                          {!a.letto && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                        </div>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.messaggio}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(a.created_at)}</span>
                          <span>da {a.created_by_nome}</span>
                        </div>
                      </div>
                    </div>
                    {hasRole(['super_admin', 'admin']) && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => handleDelete(a.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Avviso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input value={form.titolo} onChange={(e) => setForm({...form, titolo: e.target.value})} placeholder="Titolo dell'avviso" data-testid="avviso-titolo" />
            </div>
            <div className="space-y-2">
              <Label>Priorita'</Label>
              <Select value={form.priorita} onValueChange={(v) => setForm({...form, priorita: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITA.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Messaggio *</Label>
              <textarea
                className="w-full h-32 px-3 py-2 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-950"
                value={form.messaggio}
                onChange={(e) => setForm({...form, messaggio: e.target.value})}
                placeholder="Scrivi il tuo avviso qui..."
                data-testid="avviso-messaggio"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.titolo || !form.messaggio} data-testid="submit-avviso-btn">
              Invia Avviso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Avvisi;
